import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'ui/Button';
import { InputSearch } from 'ui/InputSearch';
import { InfoEmployeds } from 'components/InfoEmployeds';
import { getFormatPhone } from 'utils/getFotmatPhone';
import close from './img/iconClose.svg';
import styles from './EmployeeSelection.module.scss';
import { Box, Group, Loader, Select, SelectProps } from '@mantine/core';
import { ReactComponent as IconSearchEnter } from 'ui/InputSearch/img/enter.svg';
import { ReactComponent as CheckIcon } from './img/check.svg';
import { ReactComponent as AlertIcon } from './img/alert.svg';
import { ReactComponent as WarningIcon } from './img/iconWarning.svg';
import { ReactComponent as IconSearch } from './img/iconSearch.svg';
import { ReactComponent as IconError } from './img/iconError.svg';
import { Avatar } from 'components/Avatar';
import moment from 'moment';
import MinorAgeChecker from 'components/MinorAgeChecker';
import { GetWorkersArgs, ResponseGetWorkerType } from 'redux/Api/ordersApi/type';
import { EmployedsFilterParams, Filters } from 'pages/EmployedsContent/Filters/Filters';
import { useGetFiltersEmployedsQuery } from 'redux/Api/employedsApi/employedsApi';
import { useMediaQuery } from '@mantine/hooks';
import { MobileMenu } from 'pages/EmployedsContent/Sort/SortFilterMobile';
import ContentLoader from 'react-content-loader';
import useInfiniteScroll from 'react-infinite-scroll-hook';

type SearchParamsState = GetWorkersArgs | undefined;

const mobileMenuFiltersOnlyStub = () => undefined;

interface EmployeeSelectionProps {
  workers: ResponseGetWorkerType[] | undefined;
  selectedWorkerId: number | null;
  onWorkerSelect: (id: number | null) => void;
  error?: string | null;
  setSearachParams?: React.Dispatch<React.SetStateAction<SearchParamsState>>;
  workersFetching?: boolean;
  workersLoading?: boolean;
  hasNextPage?: boolean;
  onLoadMore?: () => void;
}

export const EmployeeSelection: React.FC<EmployeeSelectionProps> = ({
  workers,
  selectedWorkerId,
  onWorkerSelect,
  error,
  setSearachParams,
  workersFetching,
  workersLoading,
  hasNextPage,
  onLoadMore,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const { data: dataFilters } = useGetFiltersEmployedsQuery();
  const [filtersParams, setFiltersParams] = useState<EmployedsFilterParams>({
    genders: [],
    issues: [],
    nationalities: [],
    categories: [],
    contracts: [],
    limit_from: undefined,
    limit_to: undefined,
    date_add: undefined,
  });

  const isMediumWidth =
    useMediaQuery('(min-width: 1200px) and (max-width: 1406px)') ?? false;
  const isSmallWidth = useMediaQuery('(max-width: 767px)') ?? false;

  const mobileSearchRowRef = useRef<HTMLDivElement>(null);
  const [mobileDropdownWidth, setMobileDropdownWidth] = useState<number | undefined>(
    undefined,
  );

  const syncMobileSearchDropdownWidth = useCallback(() => {
    const el = mobileSearchRowRef.current;
    if (el?.offsetWidth) {
      setMobileDropdownWidth(el.offsetWidth);
    }
  }, []);

  useEffect(() => {
    syncMobileSearchDropdownWidth();
    window.addEventListener('resize', syncMobileSearchDropdownWidth);
    return () => window.removeEventListener('resize', syncMobileSearchDropdownWidth);
  }, [syncMobileSearchDropdownWidth]);

  const dataFiltersMobile = useMemo(() => {
    const f = dataFilters?.filters;
    if (!f) return undefined;
    const rows = (items: { keyword: string; title: string }[]) =>
      items?.map((item, i) => ({
        id: i + 1,
        keyword: item.keyword,
        label: item.title,
      }));
    return {
      genders: rows(f.genders),
      issues: rows(f.issues),
      nationalities: rows(f.nationalities),
      contracts: rows(f.contracts),
      categories: rows(f.categories),
    };
  }, [dataFilters]);

  const applySearchParams = (next: SearchParamsState) => {
    if (!setSearachParams) return;
    setSearachParams(next);
  };

  const [intersectionRef, { rootRef: tableScrollRootRef }] = useInfiniteScroll({
    loading: Boolean(workersLoading),
    hasNextPage: Boolean(hasNextPage),
    onLoadMore: () => onLoadMore?.(),
    rootMargin: '0px 0px 160px 0px',
    disabled: !onLoadMore,
  });

  const convertFiltersToApiParams = (filters: EmployedsFilterParams) => {
    return {
      genders: filters.genders,
      issues: filters.issues,
      categories: filters.categories?.map((c) => c.id),
      nationalities: filters.nationalities,
      contracts: filters.contracts,
      limit_from: filters.limit_from,
      limit_to: filters.limit_to,
      date_add: filters.date_add,
    };
  };

  const buildMergedParams = (nextSearch: string, nextFilters: EmployedsFilterParams) => {
    const hasSearch = Boolean(nextSearch.trim());
    const hasAnyFilters =
      (nextFilters.genders?.length ?? 0) > 0 ||
      (nextFilters.issues?.length ?? 0) > 0 ||
      (nextFilters.nationalities?.length ?? 0) > 0 ||
      (nextFilters.categories?.length ?? 0) > 0 ||
      (nextFilters.contracts?.length ?? 0) > 0 ||
      nextFilters.limit_from != null ||
      nextFilters.limit_to != null ||
      nextFilters.date_add?.start != null ||
      nextFilters.date_add?.end != null;

    if (!hasSearch && !hasAnyFilters) return undefined;

    const apiFilters = convertFiltersToApiParams(nextFilters);

    return {
      ...apiFilters,
      ...(hasSearch ? { search: nextSearch.trim() } : {}),
    };
  };

  const selectedWorker = workers?.find(
    (worker: ResponseGetWorkerType) => worker.id === selectedWorkerId,
  );

  useEffect(() => {
    if (workersLoading || workersFetching) return;
    if (!buildMergedParams(searchValue, filtersParams)) return;
    if (!selectedWorkerId) return;
    if (!selectedWorker) {
      onWorkerSelect(null);
    }
  }, [
    selectedWorker,
    selectedWorkerId,
    workersLoading,
    workersFetching,
    searchValue,
    filtersParams,
    onWorkerSelect,
  ]);

  const selectWorker = workers?.map((item: ResponseGetWorkerType) => ({
    value: item.id.toString(),
    label: `${item.surname} ${item.name} ${item.patronymic || ''} ${item.phone}`,
  }));

  const renderSelectOption: SelectProps['renderOption'] = ({ option }) => {
    const worker = workers?.find(
      (w: ResponseGetWorkerType) => w.id === Number(option.value),
    );
    if (!worker) return null;

    return (
      <Group
        flex='1'
        gap='xs'
        wrap='nowrap'
        style={{ minWidth: 0 }}
        onClick={() => onWorkerSelect(worker.id)}>
        <InfoEmployeds
          status={worker?.worker_app?.app_status}
          avatar={worker?.worker_app?.avatar ?? null}
          id={worker?.id}
          name={worker?.name}
          surname={worker?.surname}
          patronymic={worker?.patronymic}
          phone={getFormatPhone(worker?.phone)}
          withPhoneIcon={false}
        />
      </Group>
    );
  };

  const isHasSignedContract =
    selectedWorker?.unified_contract?.status === 'signed_by_worker';

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.content}>
          <div className={styles.header}>
            <p className={styles.title}>Исполнители</p>
            {selectedWorkerId && (
              <Button theme='link' icon src={close} onClick={() => onWorkerSelect(null)}>
                Сбросить
              </Button>
            )}
          </div>
          {selectedWorkerId ? (
            <div className={styles.selected}>
              <div className={styles.employee_details}>
                <div className={styles.employee_header}>
                  <Avatar
                    name={selectedWorker?.name}
                    surname={selectedWorker?.surname}
                    avatar={selectedWorker?.worker_app?.avatar ?? null}
                    id={selectedWorker?.id}
                    statusActive={''}
                    height='82px'
                    width='82px'
                    isDeleted={selectedWorker?.is_deleted}
                  />
                  <div className={styles.employee_info}>
                    <div className={styles.employee_name_wrapper}>
                      <h3 className={styles.employee_name}>
                        {selectedWorker?.surname} {selectedWorker?.name}{' '}
                        {selectedWorker?.patronymic}
                      </h3>
                      <p className={styles.employee_phone}>
                        {getFormatPhone(selectedWorker?.phone)}
                      </p>
                    </div>
                    <p className={styles.employee_inn}>ИНН {selectedWorker?.inn}</p>
                    <p className={styles.risk_level}>Риск минимальный</p>
                  </div>
                </div>

                <div className={styles.status_indicators}>
                  {isHasSignedContract && (
                    <div className={styles.status_item}>
                      <div className={styles.status_icon}>
                        <CheckIcon />
                      </div>
                      <p>Договор № УД-{selectedWorker?.unified_contract?.number}</p>
                    </div>
                  )}
                  {isHasSignedContract && (
                    <div className={styles.status_item}>
                      <div className={styles.status_icon}>
                        <CheckIcon />
                      </div>
                      <p>
                        Лимит по договору:{' '}
                        {selectedWorker?.unified_contract?.limit
                          ? `${
                              selectedWorker?.unified_contract?.total_orders_amount?.toLocaleString(
                                'ru-RU',
                              ) || 0
                            } ₽ из ${selectedWorker?.unified_contract?.limit?.toLocaleString(
                              'ru-RU',
                            )} ₽`
                          : 'Не указан'}
                      </p>
                    </div>
                  )}
                  {selectedWorker?.limit && (
                    <div className={styles.status_item}>
                      <div className={styles.status_icon}>
                        <CheckIcon />
                      </div>
                      <p>
                        Лимит на месяц:{' '}
                        {selectedWorker?.monthly_payment?.toLocaleString('ru-RU') || '0'}{' '}
                        ₽ из {selectedWorker?.limit?.toLocaleString('ru-RU')} ₽
                      </p>
                    </div>
                  )}
                  {!selectedWorker?.worker_app?.nationality ? (
                    <div className={styles.status_item}>
                      <div className={styles.status_icon}>
                        <AlertIcon />
                      </div>
                      <p>Гражданство неизвестно. Обратитесь в поддержку</p>
                    </div>
                  ) : selectedWorker?.worker_app?.nationality !== 'Россия' ? (
                    <div className={styles.status_item}>
                      <div className={styles.status_icon}>
                        <AlertIcon />
                      </div>
                      <p>
                        Не является гражданином России. Не забудь отправить уведомление в
                        МВД
                      </p>
                    </div>
                  ) : null}
                  <div className={styles.status_item}>
                    <div className={styles.status_icon}>
                      <CheckIcon />
                    </div>
                    <p>Является самозанятым</p>
                  </div>
                  {Boolean(selectedWorker?.has_ip) && (
                    <div className={styles.status_item}>
                      <div className={styles.status_icon}>
                        <WarningIcon />
                      </div>
                      <p>Есть открытое ИП</p>
                    </div>
                  )}
                  <div className={styles.status_item}>
                    <div className={styles.status_icon}>
                      <CheckIcon />
                    </div>
                    <p>Не состоит в списке экстремистов и террористов</p>
                  </div>
                  <div className={styles.status_item}>
                    <div className={styles.status_icon}>
                      <CheckIcon />
                    </div>
                    <p>Лимит дохода НПД 2,4 млн. не израсходован</p>
                  </div>
                  {selectedWorker?.is_deleted && (
                    <div className={styles.status_item}>
                      <div className={styles.status_icon}>
                        <IconError style={{ width: '24px', height: '24px' }} />
                      </div>
                      <p style={{ color: '#e10d0d' }}>Профиль удален</p>
                    </div>
                  )}
                  <div>
                    <MinorAgeChecker data={selectedWorker} showLabel={false} />
                  </div>
                </div>

                <p className={styles.report_date}>
                  Отчет на {moment().format('D MMMM YYYY')}
                </p>
              </div>
            </div>
          ) : (
            <div>
              <div className={error ? styles['error-border'] : ''}>
                <div className={styles.onlyMobile}>
                  <div ref={mobileSearchRowRef} className={styles.mobileSearchRow}>
                    <div className={styles.mobileSearchSelectWrap}>
                      <Select
                        placeholder='Искать...'
                        value={selectedWorkerId?.toString() ?? null}
                        onChange={(v) => onWorkerSelect(v ? Number(v) : null)}
                        data={selectWorker || []}
                        nothingFoundMessage='Ничего не найдено'
                        renderOption={renderSelectOption}
                        leftSection={<IconSearch />}
                        rightSection={
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            {searchValue.trim() && (
                              <Box
                                component='button'
                                type='button'
                                onPointerDown={(e: React.PointerEvent) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  applySearchParams(
                                    buildMergedParams(searchValue, filtersParams),
                                  );
                                }}
                                onTouchStart={(e: React.TouchEvent) => {
                                  e.stopPropagation();
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  padding: 0,
                                  border: 'none',
                                  background: 'none',
                                }}>
                                {workersFetching ? (
                                  <div style={{ paddingRight: '6px' }}>
                                    <Loader size='xs' color='#002cfb' />
                                  </div>
                                ) : (
                                  <IconSearchEnter style={{ width: 70, height: 18 }} />
                                )}
                              </Box>
                            )}
                          </div>
                        }
                        rightSectionPointerEvents='auto'
                        searchable
                        searchValue={searchValue}
                        onSearchChange={(value) => {
                          setSearchValue(value);
                          if (!value.trim()) {
                            applySearchParams(buildMergedParams('', filtersParams));
                          }
                        }}
                        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                          if (
                            e.key === 'Enter' &&
                            searchValue.trim() &&
                            setSearachParams
                          ) {
                            e.preventDefault();
                            applySearchParams(
                              buildMergedParams(searchValue, filtersParams),
                            );
                          }
                        }}
                        filter={({ options }) => options}
                        withErrorStyles={false}
                        styles={{
                          input: {
                            height: 48,
                            fontSize: '16px',
                            border: error ? '1px solid red' : 'none',
                            padding: '0 84px 0 44px',
                          },
                          dropdown: {
                            boxShadow: '0px 0px 10px 0px rgba(0, 32, 51, 0.10)',
                            border: 'none',
                            borderRadius: 4,
                            padding: 0,
                          },
                          option: {
                            minWidth: 0,
                          },
                          section: {
                            marginRight: '12px',
                            display: 'flex',
                            justifyContent: 'end',
                          },
                        }}
                        scrollAreaProps={
                          hasNextPage
                            ? { onBottomReached: () => onLoadMore?.() }
                            : undefined
                        }
                        comboboxProps={{
                          position: 'bottom-start',
                          middlewares: { flip: false, shift: false },
                          offset: 4,
                          width: mobileDropdownWidth,
                        }}
                        onDropdownOpen={syncMobileSearchDropdownWidth}
                        allowDeselect={false}
                      />
                    </div>
                    <div className={styles.mobileSearchFilterWrap}>
                      <MobileMenu
                        filtersOnly
                        filtersOnlyIconOnly
                        mobileTabsSort={[]}
                        handleTabClickSort={mobileMenuFiltersOnlyStub}
                        activeSortName='default'
                        setActiveSort={mobileMenuFiltersOnlyStub}
                        dataFiltersMobile={dataFiltersMobile}
                        initialFilters={{
                          genders: filtersParams.genders ?? [],
                          issues: filtersParams.issues ?? [],
                          nationalities: filtersParams.nationalities ?? [],
                          categories: filtersParams.categories ?? [],
                          contracts: filtersParams.contracts ?? [],
                          limit_from: filtersParams.limit_from,
                          limit_to: filtersParams.limit_to,
                          date_add: filtersParams.date_add,
                        }}
                        showFilters={true}
                        onApplyFilters={(nextFilters) => {
                          setFiltersParams({
                            genders: nextFilters.genders ?? [],
                            issues: nextFilters.issues ?? [],
                            nationalities: nextFilters.nationalities ?? [],
                            categories: nextFilters.categories ?? [],
                            contracts: nextFilters.contracts ?? [],
                            limit_from: nextFilters.limit_from,
                            limit_to: nextFilters.limit_to,
                            date_add: nextFilters.date_add,
                          });
                          applySearchParams(buildMergedParams(searchValue, nextFilters));
                        }}
                        onResetFilters={() => {
                          const empty: EmployedsFilterParams = {
                            genders: [],
                            issues: [],
                            nationalities: [],
                            categories: [],
                            contracts: [],
                            limit_from: undefined,
                            limit_to: undefined,
                            date_add: undefined,
                          };
                          setFiltersParams(empty);
                          applySearchParams(buildMergedParams(searchValue, empty));
                        }}
                        mobileTabs={[]}
                        handleTabClick={mobileMenuFiltersOnlyStub}
                        activeTabName=''
                        toggleSearchOpen={mobileMenuFiltersOnlyStub}
                      />
                    </div>
                  </div>
                </div>
                <div className={styles.onlyDesktop}>
                  <div className={styles.searchLine}>
                    <div style={{ width: '100%' }}>
                      <InputSearch
                        notFocus
                        value={searchValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setSearchValue(e.target.value);
                          if (!e.target.value.trim() && setSearachParams) {
                            applySearchParams(buildMergedParams('', filtersParams));
                          }
                        }}
                        onSearch={() => {
                          applySearchParams(
                            buildMergedParams(searchValue, filtersParams),
                          );
                        }}
                      />
                    </div>
                    <Filters
                      dataFilters={
                        dataFilters?.filters ?? {
                          genders: [],
                          issues: [],
                          categories: [],
                          contracts: [],
                          nationalities: [],
                        }
                      }
                      notShowText={isMediumWidth || isSmallWidth}
                      showFilters={true}
                      initialFilters={{
                        genders: filtersParams.genders ?? [],
                        issues: filtersParams.issues ?? [],
                        nationalities: filtersParams.nationalities ?? [],
                        categories: filtersParams.categories ?? [],
                        contracts: filtersParams.contracts ?? [],
                        limit_from: filtersParams.limit_from,
                        limit_to: filtersParams.limit_to,
                        date_add: filtersParams.date_add,
                      }}
                      onApplyFilters={(nextFilters) => {
                        setFiltersParams({
                          genders: nextFilters.genders ?? [],
                          issues: nextFilters.issues ?? [],
                          nationalities: nextFilters.nationalities ?? [],
                          categories: nextFilters.categories ?? [],
                          contracts: nextFilters.contracts ?? [],
                          limit_from: nextFilters.limit_from,
                          limit_to: nextFilters.limit_to,
                          date_add: nextFilters.date_add,
                        });
                        applySearchParams(buildMergedParams(searchValue, nextFilters));
                      }}
                      onResetFilters={() => {
                        const empty: EmployedsFilterParams = {
                          genders: [],
                          issues: [],
                          nationalities: [],
                          categories: [],
                          contracts: [],
                          limit_from: undefined,
                          limit_to: undefined,
                          date_add: undefined,
                        };
                        setFiltersParams(empty);
                        applySearchParams(buildMergedParams(searchValue, empty));
                      }}
                    />
                  </div>
                  <div className={styles.table} ref={tableScrollRootRef}>
                    {workersLoading ? (
                      <ContentLoader
                        speed={2}
                        height={'270px'}
                        width='100%'
                        className={styles.tab_loader}
                        backgroundColor={'#FFFFFF'}
                        foregroundColor={'#D8E4FB8C'}>
                        <rect x='0' y='0' rx='0' ry='0' width='100%' height='100%' />
                      </ContentLoader>
                    ) : (
                      <>
                        {workers?.length ? (
                          <>
                            {workers.map((worker: ResponseGetWorkerType) => (
                              <div
                                key={worker.id}
                                className={styles.row}
                                onClick={() => onWorkerSelect(worker.id)}>
                                <div className={`${styles.user} ${styles.desktop}`}>
                                  <InfoEmployeds
                                    status={worker.worker_app?.app_status}
                                    avatar={worker.worker_app?.avatar ?? null}
                                    id={worker.id}
                                    name={worker.name}
                                    surname={worker.surname}
                                    patronymic={worker.patronymic}
                                  />
                                </div>
                                <p className={styles.phone}>
                                  {getFormatPhone(worker.phone)}
                                </p>
                                <div className={styles.mobile}>
                                  <InfoEmployeds
                                    status={worker.worker_app?.app_status}
                                    avatar={worker.worker_app?.avatar ?? null}
                                    id={worker.id}
                                    name={worker.name}
                                    surname={worker.surname}
                                    patronymic={worker.patronymic}
                                    phone={getFormatPhone(worker.phone)}
                                    withPhoneIcon={false}
                                  />
                                </div>
                              </div>
                            ))}
                            {workersFetching && (
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                  padding: '10px 0',
                                }}>
                                <Loader size='sm' color='#002cfb' />
                              </div>
                            )}
                          </>
                        ) : (
                          <div className={styles.empty}>Нет исполнителей</div>
                        )}
                        {hasNextPage ? (
                          <div ref={intersectionRef} style={{ height: 10 }} />
                        ) : null}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <p className={styles.info}>
                Предложить заказ можно только готовым к сотрудничеству исполнителям
              </p>
              {error && <p className={styles['error-info']}>{error}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
